import Grid from '@mui/material/Grid';
import {
  Form_FormFieldItemType,
  type Forms_FormFieldGridWidth,
  type Forms_FormFieldItem,
} from '@tyro/api';
import { useTranslation } from '@tyro/i18n';
import { useCallback } from 'react';
/* eslint-disable react/prop-types */
import type { Control, FieldValues, Path } from 'react-hook-form';

import { RHFCheckboxGroup } from '../checkbox-group';
import { RHFDatePicker } from '../date-picker';
import { RHFDateTimePicker } from '../date-time-picker';
import { RHFRadioGroup } from '../radio-group';
import { RHFSelect } from '../select';
import { RHFTextField } from '../text-field';
import { RHFTimePicker } from '../time-picker';

type GridWidth = Omit<
  Record<keyof Forms_FormFieldGridWidth, number>,
  '__typename'
>;

type FieldProps<Fields extends FieldValues> = Omit<
  Forms_FormFieldItem,
  'id'
> & {
  id: Path<Fields>;
  control: Control<Fields>;
  readOnly?: boolean;
};

export function getCheckedGridWidth(
  gridWidth: Forms_FormFieldGridWidth,
): GridWidth {
  const gridEntries = Object.entries(gridWidth) as [
    keyof Forms_FormFieldGridWidth,
    number | null,
  ][];
  const checkedGridWidth = gridEntries.reduce<GridWidth>(
    (acc, [key, value]) => {
      if (key !== '__typename' && value) {
        acc[key] = Math.min(value, 12);
      }
      return acc;
    },
    { xs: 12 } as GridWidth,
  );
  return checkedGridWidth;
}

export const Field = <Fields extends FieldValues>({
  id,
  label,
  type,
  options,
  gridWidth,
  control,
  required,
  readOnly = false,
}: FieldProps<Fields>) => {
  const { t } = useTranslation(['common', 'forms']);

  const nonBlankLabel = required ? `${label} *` : (label ?? '');
  const PresetGrid = useCallback(
    ({ children }: { children: React.ReactNode }) => {
      const checkedGridWidth = getCheckedGridWidth(gridWidth);

      return <Grid size={checkedGridWidth}>{children}</Grid>;
    },
    [gridWidth],
  );

  switch (type) {
    case Form_FormFieldItemType.Checkbox:
      return (
        <PresetGrid>
          <RHFCheckboxGroup
            options={options}
            getOptionLabel={(option) => option.name}
            optionIdKey="id"
            controlProps={{
              name: id,
              control,
            }}
            label={nonBlankLabel}
            checkboxProps={{
              disabled: readOnly,
            }}
          />
        </PresetGrid>
      );
    case Form_FormFieldItemType.Date:
      return (
        <PresetGrid>
          <RHFDatePicker
            controlProps={{
              name: id,
              control,
            }}
            label={nonBlankLabel}
            inputProps={{
              fullWidth: true,
            }}
            datePickerProps={{
              readOnly,
            }}
          />
        </PresetGrid>
      );
    case Form_FormFieldItemType.Datetime:
      return (
        <PresetGrid>
          <RHFDateTimePicker
            controlProps={{
              name: id,
              control,
            }}
            label={nonBlankLabel}
            inputProps={{
              fullWidth: true,
            }}
            dateTimePickerProps={{
              readOnly,
            }}
          />
        </PresetGrid>
      );
    case Form_FormFieldItemType.Input:
    case Form_FormFieldItemType.Inputnumber: {
      const isNumbers = type === Form_FormFieldItemType.Inputnumber;

      return (
        <PresetGrid>
          <RHFTextField
            controlProps={{
              name: id,
              control,
            }}
            label={nonBlankLabel}
            textFieldProps={{
              fullWidth: true,
              type: isNumbers ? 'number' : 'text',
              inputProps: {
                readOnly,
              },
            }}
          />
        </PresetGrid>
      );
    }
    case Form_FormFieldItemType.Multiselect:
    case Form_FormFieldItemType.Select:
      return (
        <PresetGrid>
          <RHFSelect
            controlProps={{
              name: id,
              control,
            }}
            canDeleteValue={!required}
            label={label}
            options={options}
            optionIdKey="id"
            optionTextKey="name"
            slotProps={{
              select: {
                multiple: type === Form_FormFieldItemType.Multiselect,
                readOnly,
              },
            }}
            fullWidth
          />
        </PresetGrid>
      );
    case Form_FormFieldItemType.Radio:
      return (
        <PresetGrid>
          <RHFRadioGroup
            controlProps={{
              name: id,
              control,
            }}
            optionIdKey="id"
            optionTextKey="name"
            label={nonBlankLabel}
            options={options}
            disabled={readOnly}
          />
        </PresetGrid>
      );
    case Form_FormFieldItemType.Textarea:
      return (
        <PresetGrid>
          <RHFTextField
            label={nonBlankLabel}
            controlProps={{
              name: id,
              control,
            }}
            textFieldProps={{
              fullWidth: true,
              multiline: true,
              rows: 3,
              inputProps: {
                readOnly,
              },
            }}
          />
        </PresetGrid>
      );
    case Form_FormFieldItemType.Time:
      return (
        <PresetGrid>
          <RHFTimePicker
            label={nonBlankLabel}
            controlProps={{
              name: id,
              control,
            }}
            inputProps={{
              fullWidth: true,
            }}
            timePickerProps={{
              readOnly,
            }}
          />
        </PresetGrid>
      );
    case Form_FormFieldItemType.FormConfirmation:
      return (
        <PresetGrid>
          <RHFRadioGroup
            controlProps={{
              name: id,
              control,
            }}
            optionIdKey="id"
            optionTextKey="value"
            label={nonBlankLabel}
            options={[
              {
                id: 'true',
                value: t('forms:templatePlaceholders.formConfirmationConfirm'),
              },
              {
                id: 'false',
                value: t(
                  'forms:templatePlaceholders.formConfirmationNotConfirm',
                ),
              },
            ]}
            disabled={readOnly}
          />
        </PresetGrid>
      );
    default:
      return null;
  }
};
